import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Download, X } from 'lucide-react';

// Intervalo de verificación de versión (en ms)
const VERSION_CHECK_INTERVAL = 60000; // 1 minuto
const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export function useAutoUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [newVersion, setNewVersion] = useState(null);
  const [checking, setChecking] = useState(false);

  // Obtener versión actual guardada
  const getSavedVersion = useCallback(() => {
    return localStorage.getItem('app_version') || null;
  }, []);

  // Guardar versión actual
  const saveVersion = useCallback((version) => {
    localStorage.setItem('app_version', version);
    localStorage.setItem('app_version_checked', Date.now().toString());
  }, []);

  // Verificar si hay nueva versión
  const checkForUpdate = useCallback(async () => {
    if (checking) return;
    setChecking(true);

    try {
      // Añadir timestamp para evitar cache
      const response = await fetch(`${API}/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        console.warn('[AutoUpdate] No se pudo obtener version.json');
        return;
      }

      const data = await response.json();
      const serverVersion = data.buildId || data.version;
      const savedVersion = getSavedVersion();

      setCurrentVersion(savedVersion);
      setNewVersion(serverVersion);

      console.log(`[AutoUpdate] Versión local: ${savedVersion}, Servidor: ${serverVersion}`);

      if (!savedVersion) {
        // Primera vez - guardar versión actual
        saveVersion(serverVersion);
        setCurrentVersion(serverVersion);
      } else if (savedVersion !== serverVersion) {
        // ¡Nueva versión disponible!
        console.log('[AutoUpdate] ¡Nueva versión detectada!');
        setUpdateAvailable(true);
      }
    } catch (error) {
      console.warn('[AutoUpdate] Error verificando versión:', error);
    } finally {
      setChecking(false);
    }
  }, [checking, getSavedVersion, saveVersion]);

  // Aplicar actualización
  const applyUpdate = useCallback(async () => {
    console.log('[AutoUpdate] Aplicando actualización...');
    
    try {
      // 1. Limpiar todas las caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('[AutoUpdate] Caches eliminadas');
      }

      // 2. Desregistrar Service Workers existentes
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
        console.log('[AutoUpdate] Service Workers desregistrados');
      }

      // 3. Guardar nueva versión
      if (newVersion) {
        saveVersion(newVersion);
      }

      // 4. Forzar recarga completa
      toast.success('Actualizando aplicación...');
      
      // Pequeña espera para que se muestre el toast
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Recargar ignorando cache
      window.location.reload(true);
      
    } catch (error) {
      console.error('[AutoUpdate] Error aplicando actualización:', error);
      toast.error('Error al actualizar. Intenta recargar manualmente.');
    }
  }, [newVersion, saveVersion]);

  // Verificar al cargar y periódicamente
  useEffect(() => {
    // Verificar inmediatamente al cargar
    checkForUpdate();

    // Verificar periódicamente
    const interval = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);

    // Verificar cuando la ventana vuelve a estar visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Escuchar mensajes del Service Worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_UPDATED') {
          console.log('[AutoUpdate] Service Worker actualizado:', event.data.version);
          setUpdateAvailable(true);
          setNewVersion(event.data.version);
        }
      });
    }

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate]);

  return {
    updateAvailable,
    currentVersion,
    newVersion,
    checking,
    checkForUpdate,
    applyUpdate
  };
}

// Componente visual de notificación de actualización
export function UpdateNotification() {
  const { updateAvailable, applyUpdate, newVersion } = useAutoUpdate();
  const [dismissed, setDismissed] = useState(false);

  if (!updateAvailable || dismissed) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg shadow-2xl p-4 text-white">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-full">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-lg">¡Nueva versión disponible!</h4>
            <p className="text-sm text-white/80 mt-1">
              Hay una actualización lista para instalar. Actualiza ahora para obtener las últimas mejoras.
            </p>
            {newVersion && (
              <p className="text-xs text-white/60 mt-1">
                Versión: {newVersion}
              </p>
            )}
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-white/20 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={applyUpdate}
            className="flex-1 bg-white text-cyan-700 font-bold py-2 px-4 rounded-lg hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar Ahora
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
          >
            Después
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook para forzar limpieza completa de cache
export function useCacheClear() {
  return useCallback(async () => {
    console.log('[CacheClear] Iniciando limpieza completa...');
    
    try {
      // Limpiar caches
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
      }

      // Desregistrar SW
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }

      // Limpiar localStorage de versión para forzar re-check
      localStorage.removeItem('app_version');
      localStorage.removeItem('app_version_checked');

      toast.success('Cache limpiada correctamente');
      
      // Recargar
      setTimeout(() => window.location.reload(true), 1000);
      
    } catch (error) {
      console.error('[CacheClear] Error:', error);
      toast.error('Error al limpiar cache');
    }
  }, []);
}

export default UpdateNotification;
